import React, { ReactNode, useState, useRef, DragEvent } from 'react';
import { Card, CardContent, Typography, Box, Stack, IconButton, LinearProgress } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { v4 as uuidv4 } from 'uuid';

import { colors } from 'theme/mui/colors';
import SkeletonContainer from '../container/SkeletonContainer';
import FetchingErrorAlert from '../alert/FetchingErrorAlert';

export type UploadingFile = {
  id: string;
  name: string;
  progress: number;
  status: 'uploading' | 'completed' | 'error';
  error?: string;
  file?: File;
  controller?: AbortController;
  url?: string;
};

type Props = Partial<{
  text: ReactNode;
  hint: ReactNode;
  onDrop: (files: File[]) => void;
  multiple: boolean;
  accept: string | string[];
  noClick: boolean;
  noDrag: boolean;
  onRemoveFile: (file: UploadingFile) => void;
  onChange: (files: UploadingFile[]) => void;
  setFiles: React.Dispatch<React.SetStateAction<UploadingFile[]>>;
  files: UploadingFile[];
  isError: boolean;
  refetch?: () => void;
  isLoading?: boolean;
  isFetching?: boolean;
  isDisabled?: boolean;
}>;

const InputUploadZone = (props: Props) => {
  const {
    text,
    hint,
    onDrop,
    multiple = true,
    accept,
    noClick = false,
    noDrag = false,
    onChange,
    onRemoveFile,
    files = [],
    setFiles,
    isError,
    refetch,
    isLoading = false,
    isDisabled = false,
  } = props;

  //#region Properties
  const [isDragActive, setIsDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    onDrop?.(files);
    updateFiles(files);
  };

  const handleClick = () => {
    if (!noClick) inputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    onDrop?.(files);
    updateFiles(files);
    e.target.value = '';
  };

  const updateFiles = (newFiles: File[]) => {
    const uploadingFiles: UploadingFile[] = newFiles.map(file => ({
      id: uuidv4(),
      name: file.name,
      progress: 0,
      status: 'uploading',
      file: file,
    }));

    const updatedFiles = multiple ? [...(files ?? []), ...uploadingFiles] : uploadingFiles.slice(0, 1);
    setFiles?.(updatedFiles);
    onChange?.(updatedFiles);
  };

  const handleRemove = (fileToRemove: UploadingFile) => {
    fileToRemove.controller?.abort();
    const remainingFiles = files?.filter(f => f !== fileToRemove);

    setFiles?.(remainingFiles);
    onRemoveFile?.(fileToRemove);
  };

  return (
    <Stack spacing={2}>
      <Card
        sx={{
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.400',
          backgroundColor: isDragActive ? 'action.hover' : 'transparent',
          transition: 'border-color 0.2s ease',
          '&:hover': {
            borderColor: 'primary.light',
          },
        }}
        onDragEnter={noDrag || isDisabled ? undefined : handleDragEnter}
        onDragOver={noDrag || isDisabled ? undefined : handleDragOver}
        onDragLeave={noDrag || isDisabled ? undefined : handleDragLeave}
        onDrop={noDrag || isDisabled ? undefined : handleDrop}
        onClick={handleClick}
        elevation={0}
      >
        <CardContent>
          <input
            ref={inputRef}
            disabled={isDisabled}
            type="file"
            hidden
            multiple={multiple}
            accept={Array.isArray(accept) ? accept.join(',') : accept}
            onChange={handleFileChange}
          />
          <Box mb={2}>
            <CloudUploadIcon fontSize="large" color={isDragActive ? 'primary' : 'action'} />
          </Box>
          {text && (
            <Typography variant="body1" gutterBottom>
              {text}
            </Typography>
          )}
          {hint && (
            <Typography variant="body2" color="textSecondary">
              {hint}
            </Typography>
          )}
        </CardContent>
      </Card>

      <SkeletonContainer
        rows={{ xs: 2 }}
        columns={{ xs: 1 }}
        loading={isLoading}
        isError={isError}
        skeletonHeight={50}
        errEl={
          <Stack sx={{ marginTop: 20 }} justifyContent="center">
            <FetchingErrorAlert onRetry={refetch} />
          </Stack>
        }
      >
        {files?.length > 0 && (
          <Box mt={2} sx={files?.length > 5 ? { overflowY: 'auto', height: 240 } : {}}>
            {files?.map(file => (
              <Stack
                key={file.id}
                sx={{
                  p: 1.5,
                  border: `1px solid ${colors.gray[300]}`,
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <Stack
                  direction="row"
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    mb: file.status === 'uploading' ? 1 : 0,
                  }}
                >
                  <Typography
                    component="a"
                    variant="body2"
                    noWrap
                    download={file.url}
                    href={file.url}
                    sx={{
                      textDecoration: 'none',
                      color: file.status === 'error' ? 'error.main' : 'inherit',
                      maxWidth: { xs: '60%', sm: '70%' },
                    }}
                  >
                    {file.name}
                  </Typography>
                  <Stack direction="row" alignItems="center">
                    {file.status === 'uploading' && (
                      <Typography variant="caption" sx={{ mr: 1 }}>
                        {Math.round(file.progress)}%
                      </Typography>
                    )}
                    <IconButton size="small" disabled={isDisabled} onClick={() => handleRemove(file)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>

                {file.status === 'uploading' && (
                  <LinearProgress
                    variant="determinate"
                    value={file.progress}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      width: '100%',
                      mt: 0.5,
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 2,
                      },
                    }}
                  />
                )}

                {file.error && (
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    {file.error}
                  </Typography>
                )}
              </Stack>
            ))}
          </Box>
        )}
      </SkeletonContainer>
    </Stack>
  );
};

export default InputUploadZone;
